import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { menuImage, signatureMenu } from '@/mocks/home';
import { cdn } from '@/config/cdn';

gsap.registerPlugin(ScrollTrigger);

const STICKY_TOP = 104;

/**
 * Lune-style stage: the left wrapper is CSS-sticky (lg+), the viewport clips,
 * and one scrubbed ScrollTrigger translates the 140% inner image from its top
 * to its bottom edge across the sticky range. Mobile / reduced motion: static.
 */
export default function SignatureMenu() {
  const columnRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const column = columnRef.current;
    const sticky = stickyRef.current;
    const viewport = viewportRef.current;
    const media = mediaRef.current;
    if (!column || !sticky || !viewport || !media) return undefined;

    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      const travel = () => Math.max(0, media.offsetHeight - viewport.offsetHeight);
      const tween = gsap.fromTo(
        media,
        { y: 0 },
        {
          y: () => -travel(),
          ease: 'none',
          force3D: true,
          scrollTrigger: {
            trigger: column,
            start: `top top+=${STICKY_TOP}`,
            end: () => `bottom top+=${STICKY_TOP + sticky.offsetHeight}`,
            // 0.15s catch-up: near-immediate, but smooths discrete wheel steps
            scrub: 0.15,
            invalidateOnRefresh: true,
          },
        },
      );
      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
        gsap.set(media, { clearProps: 'transform' });
      };
    });

    const refresh = () => ScrollTrigger.refresh();
    if (!media.complete) media.addEventListener('load', refresh, { once: true });
    document.fonts?.ready.then(refresh).catch(() => undefined);

    return () => {
      media.removeEventListener('load', refresh);
      mm.revert();
    };
  }, []);

  return (
    <section className="bg-[#171515] text-[#F5F1EB] py-20 md:py-32" aria-labelledby="menu-title">
      <div className="fold-shell grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-0 items-stretch">
        <div ref={columnRef} className="relative lg:pr-16 xl:pr-20" data-parallax-column>
          <div ref={stickyRef} className="lg:sticky lg:top-[104px]" data-parallax-sticky>
            <div
              ref={viewportRef}
              className="relative w-full aspect-[4/5] md:aspect-[3/4] lg:aspect-auto lg:h-[min(calc(100vh-160px),900px)] overflow-hidden bg-[#0F0E0E]"
              data-parallax-viewport
            >
              <img
                ref={mediaRef}
                src={cdn(menuImage.src)}
                alt={menuImage.alt}
                title="FOLD. signature pastries"
                className="absolute left-0 top-0 w-full h-full lg:h-[125%] object-cover object-top will-change-transform"
                data-parallax-media
              />
            </div>
            <p className="mt-4 fold-label text-[rgba(245,241,235,0.45)]">Today&rsquo;s first tray · 06:40</p>
          </div>
        </div>

        <div className="lg:pl-16 xl:pl-20 lg:border-l border-[rgba(245,241,235,0.12)]">
          <p className="fold-label text-[rgba(245,241,235,0.5)]">Signature Menu</p>
          <h2 id="menu-title" className="fold-serif mt-6 text-4xl md:text-[56px] leading-[1.02] max-w-[520px]">
            On the counter, until it&rsquo;s gone.
          </h2>
          <ol className="mt-14 md:mt-20">
            {signatureMenu.map((item, i) => (
              <li key={item.name} className="grid grid-cols-[32px_minmax(0,1fr)_auto] gap-x-4 py-8 md:py-10 border-t border-[rgba(245,241,235,0.14)]">
                <span className="fold-label text-[rgba(245,241,235,0.4)] pt-2">0{i + 1}</span>
                <div className="min-w-0">
                  <h3 className="fold-serif text-2xl md:text-[30px] leading-tight">{item.name}</h3>
                  <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-[rgba(245,241,235,0.68)] max-w-[420px]">{item.description}</p>
                  <p className="mt-3 text-xs tracking-wide text-[rgba(245,241,235,0.4)]">{item.meta}</p>
                </div>
                <span className="text-sm tabular-nums text-[rgba(245,241,235,0.8)] pt-2">{item.price}</span>
              </li>
            ))}
          </ol>
          <div className="pt-8 border-t border-[rgba(245,241,235,0.14)] grid grid-cols-[32px_minmax(0,1fr)] gap-x-4">
            <span></span>
            <p className="text-xs leading-relaxed text-[rgba(245,241,235,0.5)] max-w-[420px]">
              Baked in an open kitchen that handles gluten, dairy, egg and nuts. No product can be considered allergen free.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}